import React, { useCallback, useRef, type ComponentProps } from 'react';
import Popover from '@splunk/react-ui/Popover';
import SimpleStatusIcon from './SimpleStatusIcon';
import type { SimpleStatusIconProps } from './SimpleStatusIcon';

export interface PopoverStatusIconProps
    extends Omit<SimpleStatusIconProps, 'elementRef' | 'onClick'> {
    open: boolean;
    anchor?: React.MutableRefObject<HTMLElement | null>;
    onRequestOpen?: SimpleStatusIconProps['onClick'];
    onRequestClose?: ComponentProps<typeof Popover>['onRequestClose'];
    children?: React.ReactNode;
    hasContent?: boolean;
}

const PopoverStatusIcon = ({
    status,
    height,
    iconSize,
    iconSizeOnHover,
    open,
    anchor,
    onRequestOpen,
    onRequestClose,
    children,
    hasContent,
}: PopoverStatusIconProps): JSX.Element | null => {
    /**
     * TODO: this is a possible bug. Since this is a ref,
     * starting with a state of open won't actually open the popover since
     * the ref hasn't resolved to an actual element yet, and the change to the ref
     * doesn't cause the popover to rerender
     * This works fine after initial mount, though.
     */
    const internalAnchor = useRef<HTMLButtonElement | null>(null);
    const setAnchor = useCallback(
        (element: HTMLButtonElement | null) => {
            if (anchor) {
                // eslint-disable-next-line no-param-reassign
                anchor.current = element;
            }

            internalAnchor.current = element;
        },
        [anchor]
    );

    if (!status) {
        return null;
    }

    return (
        <>
            <SimpleStatusIcon
                elementRef={setAnchor}
                status={status}
                height={height}
                iconSize={iconSize}
                iconSizeOnHover={iconSizeOnHover}
                onClick={hasContent ? onRequestOpen : null}
            />
            {hasContent && (
                <Popover
                    open={open}
                    anchor={internalAnchor.current}
                    onRequestClose={onRequestClose}
                    takeFocus
                    defaultPlacement="below"
                >
                    {children}
                </Popover>
            )}
        </>
    );
};

export default PopoverStatusIcon;
